
import type {
  BaseErrorResponse,
  EmptySuccessResponse,
  GetPaymentSystemListRes,
  MakePaymentReq,
  MakePaymentRes,
  ResponseModel,
  UpdatePaymentSystemReq,
  CreatePaymentSystemReq,
  GetPaymentSystemRes,
} from './data-contracts';
import type { RequestParams } from './http-client';
import { ContentType, HttpClient } from './http-client';


export default function usePaymentSystem() {
  class PaymentSystem<SecurityDataType = unknown> extends HttpClient<SecurityDataType> {
    /**
     * No description
     *
     * @tags Payment system
     * @name PaymentSystemControllerGetPaymentSystemList
     * @summary Getting a list of payment systems
     * @request GET:/api3/payment-system
     * @secure
     * @response `200` `GetPaymentSystemListRes`
     * @response `400` `BaseErrorResponse`
     */
    paymentSystemControllerGetPaymentSystemList = (
      query: {
        /**
         * @min 1
         * @max 500
         * @example 10
         */
        limit: number;
        /**
         * @min 0
         * @example 0
         */
        page: number;
        /** @example true */
        isActive?: boolean;
        /** @example true */
        isEnrollment?: boolean;
        /** @example true */
        isPayout?: boolean;
      },
      params: RequestParams = {},
    ) =>
      this.request<GetPaymentSystemListRes, BaseErrorResponse>({
        path: '/api3/payment-system',
        method: 'GET',
        query,
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Payment system
     * @name PaymentSystemControllerCreatePaymentSystem
     * @summary Create payment system
     * @request POST:/api3/payment-system
     * @secure
     * @response `200` `GetPaymentSystemRes`
     * @response `400` `BaseErrorResponse`
     */
    paymentSystemControllerCreatePaymentSystem = (data: CreatePaymentSystemReq, params: RequestParams = {}) =>
      this.request<GetPaymentSystemRes, BaseErrorResponse>({
        path: '/api3/payment-system',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Payment system
     * @name PaymentSystemControllerMakePayment
     * @summary Make a payment
     * @request POST:/api3/payment-system/payment
     * @secure
     * @response `200` `MakePaymentRes`
     * @response `400` `BaseErrorResponse`
     */
    paymentSystemControllerMakePayment = (data: MakePaymentReq, params: RequestParams = {}) =>
      this.request<MakePaymentRes, BaseErrorResponse>({
        path: '/api3/payment-system/payment',
        method: 'POST',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Payment system
     * @name PaymentSystemControllerUpdatePaymentSystem
     * @summary Update payment system
     * @request PUT:/api3/payment-system/{paymentSystemId}
     * @secure
     * @response `200` `GetPaymentSystemRes`
     * @response `400` `BaseErrorResponse`
     */
    paymentSystemControllerUpdatePaymentSystem = (
      paymentSystemId: number,
      data: UpdatePaymentSystemReq,
      params: RequestParams = {},
    ) =>
      this.request<GetPaymentSystemRes, BaseErrorResponse>({
        path: `/api3/payment-system/${paymentSystemId}`,
        method: 'PUT',
        body: data,
        secure: true,
        type: ContentType.Json,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Payment system
     * @name PaymentSystemControllerDeletePaymentSystem
     * @summary Delete payment system
     * @request DELETE:/api3/payment-system/{paymentSystemId}
     * @secure
     * @response `200` `EmptySuccessResponse`
     * @response `400` `BaseErrorResponse`
     */
    paymentSystemControllerDeletePaymentSystem = (paymentSystemId: number, params: RequestParams = {}) =>
      this.request<EmptySuccessResponse, BaseErrorResponse>({
        path: `/api3/payment-system/${paymentSystemId}`,
        method: 'DELETE',
        secure: true,
        format: 'json',
        ...params,
      });

    /**
     * No description
     *
     * @tags Payment system
     * @name PaymentSystemControllerPaymentCallback
     * @summary Process payment system callback
     * @request GET:/api3/payment-system/callback
     * @response `200` `ResponseModel`
     * @response `400` `BaseErrorResponse`
     */
    paymentSystemControllerPaymentCallback = (params: RequestParams = {}) =>
      this.request<ResponseModel, BaseErrorResponse>({
        path: '/api3/payment-system/callback',
        method: 'GET',
        format: 'json',
        ...params,
      });
  }

  return new PaymentSystem();
}
